import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { CgImage } from "react-icons/cg";
import { editorStore } from "../../store/editorStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import { userStore } from "../../store/userStore";
import addToPresentationPreview from "../../assets/add-to-presentation-preview.jpg";

interface AddToPresentationCtaProps {
  className?: string;
}

/**
 * Floating lower-right CTA that opens the same Add to presentation flow as the
 * PropertyPanel ··· menu (including the freemium paywall gate).
 * On hover, shows a preview of presentations with a short explainer.
 */
const AddToPresentationCta: React.FC<AddToPresentationCtaProps> = observer(
  ({ className }) => {
    const { t } = useTranslation();
    const locked = floorplannerStore.lockedByOther;
    const [hoverPreviewOpen, setHoverPreviewOpen] = useState(false);

    const handleClick = () => {
      if (locked) return;
      if (userStore.isFreemiumUser) {
        editorStore.showPaywall("addToProject");
        return;
      }
      editorStore.clearSelections();
      editorStore.openAddToPresentationPopup();
    };

    return (
      <div
        className={`relative ${className ?? ""}`}
        onMouseEnter={() => setHoverPreviewOpen(true)}
        onMouseLeave={() => setHoverPreviewOpen(false)}
        onFocus={() => setHoverPreviewOpen(true)}
        onBlur={() => setHoverPreviewOpen(false)}
      >
        {hoverPreviewOpen && !locked && (
          <div
            className="absolute bottom-full right-0 z-50 mb-2 w-[320px] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl"
            role="tooltip"
          >
            <img
              src={addToPresentationPreview}
              alt=""
              className="h-auto w-full object-cover object-top"
            />
            <p className="px-3 py-2.5 text-center text-xs leading-relaxed text-gray-600">
              {t("common.addToPresentationHoverHint")}
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={handleClick}
          disabled={locked}
          className="flex h-[42px] w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-boniYellowPrimary px-4 text-sm font-semibold leading-none text-blackplum shadow-md transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={t("common.nextStepAddToPresentation")}
          aria-describedby={hoverPreviewOpen ? "add-to-presentation-hint" : undefined}
        >
          <CgImage className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t("common.nextStepAddToPresentation")}</span>
        </button>
        {hoverPreviewOpen && !locked && (
          <span id="add-to-presentation-hint" className="sr-only">
            {t("common.addToPresentationHoverHint")}
          </span>
        )}
      </div>
    );
  },
);

export default AddToPresentationCta;
